import { liteClient as algoliasearch, type LiteClient } from "algoliasearch/lite";
import { createNullCache } from "@algolia/client-common";
import { createContext, type FC, type PropsWithChildren, useContext, useMemo, useState } from "react";
import { parseJson } from "@/utils/parseJson";

interface Config {
    algoliaConfig: Omit<AlgoliaConfig, "tc">;
    tc: Record<string, string>;
    algoliaRefinements: AlgoliaRefinements;
    searchClient: LiteClient;
    sorting: { label: string; value: string; code?: string }[];
    facetDisplayAttributes: string[];
    setFacetDisplayAttributes: (attributes: string[]) => void;
}

const ConfigContext = createContext<Config | undefined>(undefined);

interface ConfigProviderProps {
    domElement?: HTMLElement;
    initialConfig?: AlgoliaConfig;
    initialRefinements?: AlgoliaRefinements;
}

function parseMaybeJson(raw?: string | any): any | undefined {
    if (!raw) return undefined;
    if (typeof raw === 'object') return raw;
    if (typeof raw !== 'string') return undefined;
    try { return JSON.parse(raw); } catch { return undefined; }
}

function normalizeRefinements(input: any): AlgoliaRefinements {
    let parsed = input;
    if (typeof input === 'string') {
        try {
            parsed = JSON.parse(input);
        } catch {
            parsed = input;
        }
    }

    const list = Array.isArray(parsed?.refinementList)
        ? parsed.refinementList
        : Array.isArray(parsed)
            ? parsed
            : [];

    return { refinementList: list, hasRefinements: list.length > 0 };
}


type ShopSortingItem = {
    label: string;
    index: string;
    code?: string;
};

export const ConfigProvider: FC<PropsWithChildren<ConfigProviderProps>> = ({
                                                                               children,
                                                                               domElement,
                                                                               initialConfig,
                                                                               initialRefinements
                                                                           }) => {
    const configFromDom = useMemo(() => (domElement?.dataset?.config
        ? (parseJson(domElement.dataset.config) as AlgoliaConfig | null)
        : null), [domElement?.dataset?.config]);

    const configFromWindow = typeof window !== "undefined" ? (window as any).__CONFIG__ as AlgoliaConfig | undefined : undefined;

    const cfg = (initialConfig ?? configFromDom ?? configFromWindow) || null;

    if (!cfg) {
        console.error("[ConfigProvider] missing configuration: no data-config, no initialConfig, no window.__CONFIG__");
        throw new Error("missing configuration");
    }

    if (!cfg.algoliaAppId || !cfg.algoliaSearchKey || !cfg.algoliaIndexName) {
        console.error("[ConfigProvider] invalid Algolia credentials in cfg:", {
            appId: cfg.algoliaAppId, hasKey: !!cfg.algoliaSearchKey, index: cfg.algoliaIndexName
        });
        throw new Error("Algolia config missing (appId/searchKey/indexName)");
    }

    const refinementsFromDom = useMemo(() => parseMaybeJson(domElement?.dataset?.refinements), [domElement?.dataset?.refinements]);

    const refinementsFromWindow = useMemo(() => {
        if (typeof window === "undefined") return undefined;
        return parseMaybeJson((window as any).__REFINEMENTS__);
    }, []);
    const refinements = normalizeRefinements(
        initialRefinements ?? refinementsFromDom ?? refinementsFromWindow
    );

    const { tc: translations = {}, sorting: shopSorting = [] } = cfg;

    const [facetDisplayAttributes, setFacetDisplayAttributes] = useState<string[]>([]);

    const value = useMemo<Config>(() => {
        const searchClient = algoliasearch(cfg.algoliaAppId, cfg.algoliaSearchKey, cfg.isFundingPage ? {
            // Disable the client-side response cache on the funding page so every filter change
            // triggers a real network request (visible in the browser's Network tab for debugging).
            responsesCache: createNullCache(),
            requestsCache: createNullCache()
        } : undefined);

        const sorting: { label: string; value: string; code?: string }[] = (shopSorting as ShopSortingItem[]).map(
            (s: ShopSortingItem): { label: string; value: string; code?: string } => ({
                label: s.label,
                value: s.index,
                code: s.code
            })
        );

        const { tc: _ignoreTc, ...algoliaConfig } = cfg as any;

        return {
            algoliaConfig,
            algoliaRefinements: refinements as AlgoliaRefinements,
            searchClient,
            tc: translations as Record<string, string>,
            sorting,
            facetDisplayAttributes,
            setFacetDisplayAttributes
        };
    }, [cfg, refinements, translations, shopSorting, facetDisplayAttributes]);

    return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

export const useConfigContext = () => {
    const context = useContext(ConfigContext);
    if (!context) throw new Error("useConfigContext must be used within a ConfigProvider");
    return context;
};